/**
 * Migration service for IDE content migration
 *
 * Jai1 Source Format (Windsurf/Antigravity compatible):
 * - trigger: always_on | always | manual | glob_pattern
 *
 * The service reads frontmatter from source files and extracts the `trigger`
 * field to pass to IDE-specific transformers.
 */
import { promises as fs } from 'node:fs';
import path from 'node:path';
import matter from 'gray-matter';
import type {
    MigrateIDE,
    MigrateContentType,
    SourceContentItem,
    MigrationResult,
    ScanSummary,
    IDEMigrationConfig,
    Jai1Trigger,
} from '../types/migrate-ide.types.js';
import { IDE_MIGRATION_CONFIGS } from '../constants/ide-migration-configs.js';
import { downshiftHeadings, extractBody } from '../utils/heading-utils.js';

const CODEX_RULES_START = '<!-- JAI1:CODEX:START -->';
const CODEX_RULES_END = '<!-- JAI1:CODEX:END -->';

export class MigrateIdeService {
    private projectPath: string;
    private jai1Path: string;

    constructor(projectPath: string = process.cwd()) {
        this.projectPath = projectPath;
        this.jai1Path = path.join(projectPath, '.jai1');
    }

    /**
     * Scan .jai1/ directory for content
     */
    async scanJai1Content(): Promise<ScanSummary> {
        // Scan manual rules from .jai1/rules/
        const manualRules = await this.scanContentType('rules');

        // Scan rule preset from .jai1/rule-preset/ (source of truth for presets)
        const presetRules = await this.scanRulePreset();

        // Combine: preset rules take precedence, then manual rules
        const rules = [...presetRules, ...manualRules];

        const workflows = await this.scanContentType('workflows');
        const commands: SourceContentItem[] = [];
        const skills = await this.scanSkills();

        return {
            rules,
            workflows,
            commands,
            skills,
            totalCount: rules.length + workflows.length + commands.length + skills.length,
        };
    }

    private async scanSkills(): Promise<SourceContentItem[]> {
        const skillsDir = path.join(this.jai1Path, 'skills');
        let entries;
        try {
            entries = await fs.readdir(skillsDir, { withFileTypes: true });
        } catch {
            return [];
        }

        const skills: SourceContentItem[] = [];
        for (const entry of entries.sort((a, b) => a.name.localeCompare(b.name))) {
            if (!entry.isDirectory()) continue;
            const skillDir = path.join(skillsDir, entry.name);
            const skillFile = path.join(skillDir, 'SKILL.md');
            try {
                const content = await fs.readFile(skillFile, 'utf-8');
                const { data } = matter(content);
                skills.push({
                    type: 'skills',
                    name: entry.name,
                    filepath: skillDir,
                    relativePath: path.relative(this.projectPath, skillDir),
                    description: typeof data.description === 'string' ? data.description : undefined,
                    alwaysApply: false,
                });
            } catch {
                // A valid skill directory must contain a readable SKILL.md.
            }
        }
        return skills;
    }

    /**
     * Scan .jai1/rule-preset/ directory for rule preset files
     */
    private async scanRulePreset(): Promise<SourceContentItem[]> {
        const items: SourceContentItem[] = [];
        const presetDir = path.join(this.jai1Path, 'rule-preset');

        try {
            await fs.access(presetDir);
        } catch {
            return items;
        }

        const files = await fs.readdir(presetDir);

        for (const file of files) {
            // Rule preset files can be .md or .mdc format
            if (!file.endsWith('.md') && !file.endsWith('.mdc')) continue;
            // Skip preset.json
            if (file === 'preset.json') continue;

            const filepath = path.join(presetDir, file);
            const stat = await fs.stat(filepath);
            if (!stat.isFile()) continue;

            const content = await fs.readFile(filepath, 'utf-8');

            // Parse frontmatter if exists (mdc files may have frontmatter)
            let frontmatter: Record<string, unknown> = {};
            try {
                const { data } = matter(content);
                frontmatter = data;
            } catch {
                // No frontmatter, that's fine
            }

            const name = file.endsWith('.mdc')
                ? path.basename(file, '.mdc')
                : path.basename(file, '.md');

            // Extract trigger from frontmatter (Jai1 source format)
            const trigger = this.extractTrigger(frontmatter);

            // Determine alwaysApply based on trigger
            const alwaysApply = this.isAlwaysTrigger(trigger);

            items.push({
                type: 'rules',
                name,
                filepath,
                relativePath: path.relative(this.projectPath, filepath),
                description: frontmatter.description as string | undefined,
                globs: this.extractGlobs(frontmatter),
                trigger,
                alwaysApply,
            });
        }

        return items;
    }

    /**
     * Scan a specific content type
     */
    private async scanContentType(type: MigrateContentType): Promise<SourceContentItem[]> {
        const items: SourceContentItem[] = [];
        const dirPath = path.join(this.jai1Path, type);

        try {
            await fs.access(dirPath);
        } catch {
            return items;
        }

        // Recursively collect all .md files (supports both flat and subdirectory layouts)
        const mdFiles = await this.collectMdFiles(dirPath, dirPath);

        for (const { filepath, relativeName } of mdFiles) {
            const content = await fs.readFile(filepath, 'utf-8');

            let frontmatter: Record<string, unknown> = {};
            try {
                const { data } = matter(content);
                frontmatter = data;
            } catch {
                // Skip files with invalid YAML frontmatter
                console.warn(`⚠️  Skipping ${relativeName}: Invalid YAML frontmatter`);
                continue;
            }

            // Name preserves subdirectory prefix: 'commit-it' (flat) or 'dev/feature' (subdir)
            const name = relativeName;

            // Extract trigger from frontmatter (Jai1 source format)
            const trigger = this.extractTrigger(frontmatter);

            // Determine alwaysApply based on trigger
            const alwaysApply = this.isAlwaysTrigger(trigger);

            items.push({
                type,
                name,
                filepath,
                relativePath: path.relative(this.projectPath, filepath),
                description: frontmatter.description as string | undefined,
                globs: this.extractGlobs(frontmatter),
                trigger,
                alwaysApply,
            });
        }

        return items;
    }

    /**
     * Generate copied content for IDE rule file.
     * Reads the actual source file and combines IDE-specific frontmatter with the body content.
     */
    async generateCopiedContent(
        ide: MigrateIDE,
        sourceItem: SourceContentItem
    ): Promise<string> {
        const config = IDE_MIGRATION_CONFIGS[ide];
        if (!config) throw new Error(`Unknown IDE: ${ide}`);

        // Generate IDE-specific frontmatter
        const frontmatter = config.generateFrontmatter({
            description: sourceItem.description,
            globs: sourceItem.globs,
            trigger: sourceItem.trigger,
            alwaysApply: sourceItem.alwaysApply,
            sourceFile: sourceItem.relativePath,
        });

        // Read actual source file content and strip its frontmatter
        const sourceContent = await fs.readFile(sourceItem.filepath, 'utf-8');
        const bodyContent = extractBody(sourceContent);

        // Combine IDE-specific frontmatter with actual content
        if (frontmatter) {
            return `${frontmatter}\n\n${bodyContent}\n`;
        }
        return `${bodyContent}\n`;
    }

    /**
     * Migrate content to specific IDEs
     */
    async migrate(
        ides: MigrateIDE[],
        contentTypes: MigrateContentType[],
        content: ScanSummary,
        onProgress?: (result: MigrationResult) => void
    ): Promise<MigrationResult[]> {
        const results: MigrationResult[] = [];
        const syncedSkillTargets = new Set<string>();

        for (const ide of ides) {
            const config = IDE_MIGRATION_CONFIGS[ide];
            if (!config) continue;

            // Special handling for OpenCode rules: merge all rules into AGENTS.md
            if (ide === 'opencode' && contentTypes.includes('rules') && content.rules.length > 0) {
                const agentsResult = await this.migrateOpenCodeRules(content.rules);
                results.push(agentsResult);
                onProgress?.(agentsResult);
            }

            // Codex prompt instructions live in AGENTS.md; workflows are exposed as skills.
            if (ide === 'codex') {
                if (contentTypes.includes('rules') && content.rules.length > 0) {
                    const agentsResult = await this.migrateCodexRules(content.rules);
                    results.push(agentsResult);
                    onProgress?.(agentsResult);
                }

                const skillItems = [
                    ...(contentTypes.includes('workflows') ? content.workflows : []),
                    ...(contentTypes.includes('commands') ? content.commands : []),
                ];

                for (const item of skillItems) {
                    const result = await this.migrateCodexWorkflowSkill(item);
                    results.push(result);
                    onProgress?.(result);
                }
            }

            // Migrate each content type
            for (const type of contentTypes) {
                // Skip rules for OpenCode (already handled above)
                if (ide === 'opencode' && type === 'rules') {
                    continue;
                }

                if (ide === 'codex') {
                    if (type === 'skills') {
                        for (const item of content.skills) {
                            const targetPath = this.getTargetPath(config, item);
                            if (!targetPath) continue;
                            if (syncedSkillTargets.has(targetPath)) {
                                const result: MigrationResult = {
                                    source: item,
                                    targetIDE: ide,
                                    targetPath,
                                    status: 'skipped',
                                    error: 'Skill target already synced by another selected IDE',
                                };
                                results.push(result);
                                onProgress?.(result);
                                continue;
                            }
                            syncedSkillTargets.add(targetPath);
                            const result = await this.migrateSkill(ide, config, item);
                            results.push(result);
                            onProgress?.(result);
                        }
                    }
                    continue;
                }

                const items = type === 'rules' ? content.rules :
                    type === 'workflows' ? content.workflows :
                        type === 'commands' ? content.commands :
                            content.skills;

                for (const item of items) {
                    const targetPath = this.getTargetPath(config, item);
                    if (type === 'skills' && targetPath && syncedSkillTargets.has(targetPath)) {
                        const result: MigrationResult = {
                            source: item,
                            targetIDE: ide,
                            targetPath,
                            status: 'skipped',
                            error: 'Skill target already synced by another selected IDE',
                        };
                        results.push(result);
                        onProgress?.(result);
                        continue;
                    }
                    if (type === 'skills' && targetPath) syncedSkillTargets.add(targetPath);
                    const result = type === 'skills'
                        ? await this.migrateSkill(ide, config, item)
                        : await this.migrateItem(ide, config, item);
                    results.push(result);
                    onProgress?.(result);
                }
            }
        }

        return results;
    }

    /**
     * Migrate rules for Codex by replacing only the Jai1-managed block in AGENTS.md.
     */
    private async migrateCodexRules(rules: SourceContentItem[]): Promise<MigrationResult> {
        const agentsPath = path.join(this.projectPath, 'AGENTS.md');

        try {
            let existing = '';
            let status: 'created' | 'updated' = 'created';

            try {
                existing = await fs.readFile(agentsPath, 'utf-8');
                status = 'updated';
            } catch {
                existing = '# AGENTS.md\n';
            }

            const block = await this.generateCodexAgentsBlock(rules);
            const content = this.replaceManagedBlock(existing, CODEX_RULES_START, CODEX_RULES_END, block);

            await fs.writeFile(agentsPath, content, 'utf-8');

            return {
                source: {
                    type: 'rules',
                    name: 'AGENTS',
                    filepath: agentsPath,
                    relativePath: 'AGENTS.md',
                    description: `Merged ${rules.length} rules for Codex`,
                    alwaysApply: true,
                },
                targetIDE: 'codex',
                targetPath: agentsPath,
                status,
            };
        } catch (error) {
            return {
                source: {
                    type: 'rules',
                    name: 'AGENTS',
                    filepath: agentsPath,
                    relativePath: 'AGENTS.md',
                    alwaysApply: true,
                },
                targetIDE: 'codex',
                targetPath: agentsPath,
                status: 'error',
                error: error instanceof Error ? error.message : 'Unknown error',
            };
        }
    }

    /**
     * Convert a Jai1 workflow or command into a Codex skill.
     */
    private async migrateCodexWorkflowSkill(item: SourceContentItem): Promise<MigrationResult> {
        const slug = this.toCodexSkillSlug(item.name);
        const skillDir = path.join(this.projectPath, '.agents', 'skills', slug);
        const skillPath = path.join(skillDir, 'SKILL.md');

        try {
            let status: 'created' | 'updated' = 'created';
            try {
                await fs.access(skillPath);
                status = 'updated';
            } catch {
                // Skill does not exist yet.
            }

            const sourceContent = await fs.readFile(item.filepath, 'utf-8');
            const body = extractBody(sourceContent).trim();
            const description = item.description || `Jai1 workflow: ${item.name}`;
            const skill = [
                '---',
                `name: ${slug}`,
                `description: ${this.toYamlScalar(description)}`,
                '---',
                '',
                `# Jai1 Workflow: ${item.name}`,
                '',
                'This Codex skill was generated from a Jai1 workflow. Follow the workflow instructions below.',
                '',
                body,
                '',
            ].join('\n');

            await fs.mkdir(skillDir, { recursive: true });
            await fs.writeFile(skillPath, skill, 'utf-8');

            return {
                source: item,
                targetIDE: 'codex',
                targetPath: skillPath,
                status,
            };
        } catch (error) {
            return {
                source: item,
                targetIDE: 'codex',
                targetPath: skillPath,
                status: 'error',
                error: error instanceof Error ? error.message : 'Unknown error',
            };
        }
    }

    private async generateCodexAgentsBlock(rules: SourceContentItem[]): Promise<string> {
        const lines = [
            CODEX_RULES_START,
            '## Jai1 Codex Rules',
            '',
            'These instructions are managed by Jai1. Edit source rules in `.jai1/` and run `jai1 ide sync --ides codex` to regenerate this block.',
            '',
        ];

        for (const rule of rules) {
            const content = await fs.readFile(rule.filepath, 'utf-8');
            const body = extractBody(content).trim();
            if (body) {
                lines.push(downshiftHeadings(body));
                lines.push('');
            }
        }

        lines.push(CODEX_RULES_END);
        return `${lines.join('\n')}\n`;
    }

    private replaceManagedBlock(existing: string, start: string, end: string, block: string): string {
        const normalized = existing.trimEnd();
        const pattern = new RegExp(`${this.escapeRegExp(start)}[\\s\\S]*?${this.escapeRegExp(end)}\\n?`);

        if (pattern.test(existing)) {
            return existing.replace(pattern, block);
        }

        return `${normalized}\n\n${block}`;
    }

    private toCodexSkillSlug(name: string): string {
        return name
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
    }

    private toYamlScalar(value: string): string {
        if (/^[A-Za-z0-9][A-Za-z0-9 .,_/-]*$/.test(value)) {
            return value;
        }
        return JSON.stringify(value);
    }

    private escapeRegExp(value: string): string {
        return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    /**
     * Migrate rules for OpenCode by creating/updating AGENTS.md
     */
    private async migrateOpenCodeRules(rules: SourceContentItem[]): Promise<MigrationResult> {
        const agentsPath = path.join(this.projectPath, 'AGENTS.md');

        try {
            // Check if AGENTS.md exists
            let status: 'created' | 'updated' = 'created';
            try {
                await fs.access(agentsPath);
                status = 'updated';
            } catch {
                // File doesn't exist, will be created
            }

            // Generate AGENTS.md content by merging all rules with heading downshift
            const lines = [
                '# AGENTS.md',
                '',
            ];

            for (const rule of rules) {
                const content = await fs.readFile(rule.filepath, 'utf-8');
                const body = extractBody(content);
                if (body.trim()) {
                    lines.push(downshiftHeadings(body));
                    lines.push('');
                }
            }

            lines.push('');

            await fs.writeFile(agentsPath, lines.join('\n'), 'utf-8');

            return {
                source: {
                    type: 'rules',
                    name: 'AGENTS',
                    filepath: agentsPath,
                    relativePath: 'AGENTS.md',
                    description: `Merged ${rules.length} rules for OpenCode`,
                    alwaysApply: true,
                },
                targetIDE: 'opencode',
                targetPath: agentsPath,
                status,
            };
        } catch (error) {
            return {
                source: {
                    type: 'rules',
                    name: 'AGENTS',
                    filepath: agentsPath,
                    relativePath: 'AGENTS.md',
                    alwaysApply: true,
                },
                targetIDE: 'opencode',
                targetPath: agentsPath,
                status: 'error',
                error: error instanceof Error ? error.message : 'Unknown error',
            };
        }
    }

    /**
     * Migrate a single item
     */
    private async migrateItem(
        ide: MigrateIDE,
        config: IDEMigrationConfig,
        item: SourceContentItem
    ): Promise<MigrationResult> {
        try {
            // Determine target path
            const targetPath = this.getTargetPath(config, item);

            if (!targetPath) {
                return {
                    source: item,
                    targetIDE: ide,
                    targetPath: '',
                    status: 'skipped',
                    error: `IDE ${config.id} does not support ${item.type}`,
                };
            }

            // Ensure directory exists
            const targetDir = path.dirname(targetPath);
            await fs.mkdir(targetDir, { recursive: true });

            // Check if file exists
            let status: 'created' | 'updated' = 'created';
            try {
                await fs.access(targetPath);
                status = 'updated';
            } catch {
                // File doesn't exist, will be created
            }

            // Generate copied content (actual source content with IDE-specific frontmatter)
            const copiedContent = await this.generateCopiedContent(ide, item);

            // Write file
            await fs.writeFile(targetPath, copiedContent, 'utf-8');

            return {
                source: item,
                targetIDE: ide,
                targetPath,
                status,
            };
        } catch (error) {
            return {
                source: item,
                targetIDE: ide,
                targetPath: '',
                status: 'error',
                error: error instanceof Error ? error.message : 'Unknown error',
            };
        }
    }

    private async migrateSkill(
        ide: MigrateIDE,
        config: IDEMigrationConfig,
        item: SourceContentItem,
    ): Promise<MigrationResult> {
        const targetPath = this.getTargetPath(config, item) ?? '';
        if (!targetPath) {
            return { source: item, targetIDE: ide, targetPath, status: 'skipped', error: `IDE ${ide} does not support skills` };
        }

        try {
            let status: 'created' | 'updated' = 'created';
            try {
                await fs.access(targetPath);
                status = 'updated';
            } catch {
                // Target does not exist yet.
            }
            await fs.rm(targetPath, { recursive: true, force: true });
            await fs.mkdir(path.dirname(targetPath), { recursive: true });
            await fs.cp(item.filepath, targetPath, { recursive: true });
            return { source: item, targetIDE: ide, targetPath, status };
        } catch (error) {
            return {
                source: item,
                targetIDE: ide,
                targetPath,
                status: 'error',
                error: error instanceof Error ? error.message : 'Unknown error',
            };
        }
    }

    /**
     * Get target file path for an item
     */
    private getTargetPath(config: IDEMigrationConfig, item: SourceContentItem): string | null {
        let contentPath: string | null = null;
        let extension = config.fileExtension;

        switch (item.type) {
            case 'rules':
                contentPath = config.rulesPath;
                // Rules always use fileExtension
                break;
            case 'workflows':
                // For IDEs without workflows (like Claude Code), map workflows to commands
                contentPath = config.workflowsPath ?? config.commandsPath;
                // Use workflowsExtension if available, otherwise fallback to fileExtension
                extension = config.workflowsExtension ?? config.fileExtension;
                break;
            case 'commands':
                contentPath = config.commandsPath;
                // Commands use workflowsExtension if available, otherwise fileExtension
                extension = config.workflowsExtension ?? config.fileExtension;
                break;
            case 'skills':
                contentPath = config.skillsPath;
                extension = '';
                break;
        }

        if (!contentPath) {
            return null;
        }

        // Flatten subdirectory separators for IDEs that don't support nested workflow dirs
        // e.g., 'dev/feature' → 'dev:feature' when flattenSubdirs is true
        const itemName = config.flattenSubdirs
            ? item.name.replace(/\//g, ':')
            : item.name;

        const filename = `${itemName}${extension}`;
        return path.join(this.projectPath, config.basePath, contentPath, filename);
    }

    // Helper methods

    /**
     * Recursively collect .md files from a directory.
     * Supports both flat layout (core v1) and subdirectory layout (core v2).
     * Returns relative name preserving subdirectory structure:
     *   - Flat: 'commit-it' (from commit-it.md)
     *   - Subdir: 'dev/feature' (from dev/feature.md)
     */
    private async collectMdFiles(
        rootDir: string,
        currentDir: string
    ): Promise<Array<{ filepath: string; relativeName: string }>> {
        const results: Array<{ filepath: string; relativeName: string }> = [];

        let entries;
        try {
            entries = await fs.readdir(currentDir, { withFileTypes: true });
        } catch {
            return results;
        }

        for (const entry of entries) {
            const fullPath = path.join(currentDir, entry.name);

            if (entry.isDirectory()) {
                // Recurse into subdirectories
                const subResults = await this.collectMdFiles(rootDir, fullPath);
                results.push(...subResults);
            } else if (entry.isFile() && entry.name.endsWith('.md')) {
                // Convert path to relative name without .md extension
                const relativePath = path.relative(rootDir, fullPath);
                const relativeName = relativePath.replace(/\.md$/, '');
                results.push({ filepath: fullPath, relativeName });
            }
        }

        return results;
    }


    /**
     * Extract trigger from frontmatter
     * Jai1 source format uses 'trigger' field (Windsurf/Antigravity compatible)
     */
    private extractTrigger(frontmatter: Record<string, unknown>): Jai1Trigger | undefined {
        const trigger = frontmatter.trigger;
        if (typeof trigger === 'string') {
            return trigger as Jai1Trigger;
        }

        // Fallback: check alwaysApply for Cursor-style frontmatter
        if (frontmatter.alwaysApply === true) {
            return 'always_on';
        }

        // Fallback: check globs for file-specific rules
        const globs = this.extractGlobs(frontmatter);
        if (globs && globs.length > 0) {
            return globs[0] as Jai1Trigger;
        }

        return undefined;
    }

    /**
     * Extract globs from frontmatter
     */
    private extractGlobs(frontmatter: Record<string, unknown>): string[] | undefined {
        const globs = frontmatter.globs;
        if (typeof globs === 'string') return [globs];
        if (Array.isArray(globs)) return globs as string[];
        return undefined;
    }

    /**
     * Check if trigger indicates "always apply"
     */
    private isAlwaysTrigger(trigger?: Jai1Trigger): boolean {
        return trigger === 'always' || trigger === 'always_on';
    }
}
